// Global Instructions Rule Applied!

import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Alert, Button as AntButton, Drawer, Spin, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'

import { portalGradients } from '../../../portal-configs/colors.js'
import type { RootState } from '../../../state_manager/store.ts'
import { fetchCustomizationContext } from '../services/documentReportsService.ts'
import type { CustomizationContext } from '../../../state_manager/data_models/document_report_customization_model.ts'
import { PerformanceCustomizeBody } from './PerformanceCustomizeBody.tsx'
import { FeedbackCustomizeBody } from './FeedbackCustomizeBody.tsx'

interface CustomizeReportDrawerProps {
  open: boolean
  onClose: () => void
  reportCode: string
  /** The APPLIED parameter values — the run the preview is showing. */
  params: Record<string, unknown>
  /** Called after a successful save so the parent can refresh the preview. */
  onSaved: () => void
}

/**
 * The Customize drawer for a document report. Edits are shared per
 * (report, subject, period) — the deck goes to an LP, so everyone previews
 * and prints the same edited document. In-preview editing was explicitly
 * rejected — this drawer is the single editing surface.
 *
 * The shell owns the drawer chrome and the context load; WHICH editing body
 * mounts is decided by the server-emitted `variant` discriminator on the
 * context (single-sourced from the backend's customization registry, never a
 * report-code switch here):
 *   performance_highlights -> PerformanceCustomizeBody (highlights + drivers)
 *   feedback_exit_outlook  -> FeedbackCustomizeBody (flags + exit outlook)
 */
export function CustomizeReportDrawer({
  open,
  onClose,
  reportCode,
  params,
  onSaved
}: CustomizeReportDrawerProps) {
  const accessToken = useSelector((state: RootState) => state.auth?.accessToken)

  const [context, setContext] = useState<CustomizationContext | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setContext(null)
    void fetchCustomizationContext(
      { reportCode, params },
      accessToken ?? null
    ).then((result) => {
      if (cancelled) return
      if (result.success && result.context) {
        setContext(result.context)
      } else {
        setError(result.error || 'Failed to load customization')
      }
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [open, reportCode, params, accessToken])

  return (
    <Drawer
      title={
        <div className='flex w-full min-w-0 items-center justify-between gap-2'>
          <span className='min-w-0 flex-1 truncate text-base font-semibold leading-snug text-white'>
            Customize Report
          </span>
          <Tooltip title='Close'>
            <AntButton
              type='default'
              size='small'
              icon={<FontAwesomeIcon icon={faTimes} />}
              onClick={onClose}
              aria-label='Close'
              className='flex-shrink-0 bg-white/20 !border-white/40 !text-white hover:!bg-white/30 hover:!border-white/60 hover:!text-white'
              style={{ width: 32, height: 32, padding: 0, minWidth: 0 }}
            />
          </Tooltip>
        </div>
      }
      placement='right'
      width={480}
      open={open}
      onClose={onClose}
      closable={false}
      classNames={{
        header:
          'shadow-lg !border-b-0 !h-10 !min-h-10 !max-h-10 !p-0 !px-4 flex items-center [&_.ant-drawer-header-title]:!m-0 [&_.ant-drawer-header-title]:!w-full [&_.ant-drawer-header-title]:!flex-1 [&_.ant-drawer-header-title]:!flex [&_.ant-drawer-header-title]:min-w-0 [&_.ant-drawer-header-title]:items-center'
      }}
      styles={{
        header: {
          background: portalGradients.navbar,
          height: 40,
          minHeight: 40,
          maxHeight: 40,
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          borderBottom: 'none',
          boxSizing: 'border-box'
        },
        content: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
        body: {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          padding: '8px 16px 16px'
        }
      }}
    >
      {loading ? (
        <div className='flex flex-1 items-center justify-center'>
          <Spin />
        </div>
      ) : error ? (
        <Alert type='error' showIcon message='Could not load customization' description={error} />
      ) : context?.variant === 'performance_highlights' ? (
        <PerformanceCustomizeBody
          // Remount on a new context so drafts reseed from the fresh load.
          key={`${reportCode}-${context.period.start}-${context.period.end}`}
          context={context}
          reportCode={reportCode}
          accessToken={accessToken ?? null}
          onClose={onClose}
          onSaved={onSaved}
        />
      ) : context?.variant === 'feedback_exit_outlook' ? (
        <FeedbackCustomizeBody
          key={`${reportCode}-${context.period.start}-${context.period.end}`}
          context={context}
          reportCode={reportCode}
          accessToken={accessToken ?? null}
          onClose={onClose}
          onSaved={onSaved}
        />
      ) : null}
    </Drawer>
  )
}
